import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Phone, ChevronDown, Eye,
  MapPin, Users, Briefcase, HelpCircle, Award, FileText,
  Star, Shield, Building2, Mail, DollarSign
} from 'lucide-react';
import { CartDrawer } from '@/components/common/CartDrawer';
import { useTemplate } from '@/context/TemplateContext';
import { templateList } from '@/config/templates';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DynamicIcon from '@/components/common/DynamicIcon';
import { PromoBanner } from '@/components/common/PromoBanner';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { supabase } from '@/integrations/pms/compat';

type MegaMenuSection = 'services' | 'company' | 'resources' | null;

export const Header: React.FC = () => {
  const { template, templateId, setTemplateId, previewMode, setPreviewMode } = useTemplate();
  const { settings: integrationSettings } = useIntegrationSettings();

  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await supabase
        .from('business_settings')
        .select('logo_url, company_name')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const logoUrl = bizSettings?.logo_url;
  const companyName = bizSettings?.company_name || template.brand.name;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<MegaMenuSection>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMegaMenu(null);
  }, [location.pathname]);

  const currentTemplate = templateList.find(t => t.id === templateId);

  const handleMegaMenuEnter = (section: MegaMenuSection) => {
    setActiveMegaMenu(section);
  };

  const handleMegaMenuLeave = () => {
    setActiveMegaMenu(null);
  };

  const closeMegaMenu = () => {
    setActiveMegaMenu(null);
    setIsMobileMenuOpen(false);
  };

  // Get services from template
  const services = template.services || [];
  const serviceAreas = template.serviceAreas || [];

  return (
    <>
      {/* Promo Banner */}
      <PromoBanner variant="banner" />
      
      <header
        className={cn(
          'fixed left-0 right-0 z-50 transition-all duration-300',
          isScrolled
            ? 'top-0 bg-background/95 backdrop-blur-md shadow-sm border-b border-border'
            : 'top-10 bg-background/85 backdrop-blur-md border-b border-border/40'
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              onClick={closeMegaMenu}
              className="flex items-center gap-2 font-heading font-bold text-lg lg:text-xl text-foreground hover:text-primary transition-colors"
            >
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-8 lg:h-10 w-auto object-contain" />
              ) : (
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm lg:text-base">
                    {companyName.charAt(0)}
                  </span>
                </div>
              )}
              <span className="hidden sm:inline">{companyName}</span>
            </Link>

            {/* Desktop Navigation - Mega Menu */}
            <nav className="hidden lg:flex items-center gap-1">
              {/* Services Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => handleMegaMenuEnter('services')}
                onMouseLeave={handleMegaMenuLeave}
              >
                <button className={cn(
                  'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  location.pathname === '/services'
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground hover:text-primary hover:bg-muted/60'
                )}>
                  Services
                  <ChevronDown className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    activeMegaMenu === 'services' && 'rotate-180'
                  )} />
                </button>
                
                <AnimatePresence>
                  {activeMegaMenu === 'services' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 w-[650px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-[100]"
                    >
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Briefcase className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-lg text-foreground">Our Services</h3>
                            <p className="text-sm text-muted-foreground">Professional solutions for every need</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          {services.slice(0, 6).map((service) => (
                            <Link
                              key={service.slug}
                              to={`/services#${service.slug}`}
                              onClick={closeMegaMenu}
                              className="group flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                            >
                              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                                <DynamicIcon name={service.icon} className="h-5 w-5 text-primary" />
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                                  {service.title}
                                </h4>
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                  {service.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                          <Link 
                            to="/services" 
                            onClick={closeMegaMenu}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                          >
                            View all services →
                          </Link>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Shield className="h-4 w-4 text-primary" />
                            <span>Licensed & Insured</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Company Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => handleMegaMenuEnter('company')}
                onMouseLeave={handleMegaMenuLeave}
              >
                 <button className={cn(
                   'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                   ['/about', '/service-areas', '/careers'].includes(location.pathname)
                     ? 'text-primary bg-primary/10'
                     : 'text-foreground hover:text-primary hover:bg-muted/60'
                 )}>
                  Company
                  <ChevronDown className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    activeMegaMenu === 'company' && 'rotate-180'
                  )} />
                </button>
                
                <AnimatePresence>
                  {activeMegaMenu === 'company' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 w-[420px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-[100]"
                    >
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-lg text-foreground">About {template.brand.name}</h3>
                            <p className="text-sm text-muted-foreground">{template.stats.yearsInBusiness}+ years of excellence</p>
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <Link
                            to="/about"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <Users className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">About Us</h4>
                              <p className="text-xs text-muted-foreground">Our story, mission & values</p>
                            </div>
                          </Link>
                          
                          <Link
                            to="/service-areas"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <MapPin className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">Service Areas</h4>
                              <p className="text-xs text-muted-foreground">
                                {serviceAreas.slice(0, 3).map(a => typeof a === 'string' ? a : a.name).join(', ')}{serviceAreas.length > 3 && ' & more'}
                              </p>
                            </div>
                          </Link>
                          
                          <Link
                            to="/careers"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <Briefcase className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">Careers</h4>
                              <p className="text-xs text-muted-foreground">Join our growing team</p>
                            </div>
                          </Link>
                        </div>
                        
                        {/* Trust badges */}
                        <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Star className="h-4 w-4 text-primary" />
                            <span>{template.stats.googleRating} Rating ({template.stats.reviewCount} reviews)</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Shield className="h-4 w-4 text-primary" />
                            <span>Licensed & Insured</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Resources Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => handleMegaMenuEnter('resources')}
                onMouseLeave={handleMegaMenuLeave}
              >
                 <button className={cn(
                   'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                   location.pathname === '/resources'
                     ? 'text-primary bg-primary/10'
                     : 'text-foreground hover:text-primary hover:bg-muted/60'
                 )}>
                  Resources
                  <ChevronDown className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    activeMegaMenu === 'resources' && 'rotate-180'
                  )} />
                </button>
                
                <AnimatePresence>
                  {activeMegaMenu === 'resources' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 w-[350px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-[100]"
                    >
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-lg text-foreground">Resources</h3>
                            <p className="text-sm text-muted-foreground">Helpful information & tips</p>
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <Link
                            to="/resources#faq"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <HelpCircle className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">FAQ</h4>
                              <p className="text-xs text-muted-foreground">Common questions answered</p>
                            </div>
                          </Link>
                          
                          <Link
                            to="/promos"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <Award className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">Promos</h4>
                              <p className="text-xs text-muted-foreground">Current promotions & discounts</p>
                            </div>
                          </Link>
                          
                          <Link
                            to="/resources"
                            onClick={closeMegaMenu}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              <FileText className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">Blog & Tips</h4>
                              <p className="text-xs text-muted-foreground">Expert advice & guides</p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Direct Contact Link */}
              <Link
                to="/contact"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  location.pathname === '/contact'
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground/80 hover:text-primary hover:bg-muted'
                )}
              >
                Contact
              </Link>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Preview mode exit button */}
              {previewMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewMode(false)}
                  className="hidden md:flex gap-2"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden lg:inline">Exit Preview</span>
                </Button>
              )}

              {/* Phone number */}
              <a
                href={`tel:${template.contact.phone.replace(/\D/g, '')}`}
                className="hidden sm:flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="hidden xl:inline">{template.contact.phone}</span>
              </a>

              {/* Financing CTA */}
              {integrationSettings.financing_enabled && integrationSettings.financing_url && (
                <Button asChild variant="outline" size="sm" className="hidden md:inline-flex gap-1.5">
                  <a href={integrationSettings.financing_url} target="_blank" rel="noopener noreferrer">
                    <DollarSign className="h-3.5 w-3.5" />
                    {integrationSettings.financing_cta_text || 'Apply for Financing'}
                  </a>
                </Button>
              )}

              {/* Cart */}
              <CartDrawer />

              {/* CTA Button */}
              <Button asChild size="sm" className="hidden md:inline-flex">
                <Link to="/contact">Get Quote</Link>
              </Button>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-popover border-t border-border overflow-hidden"
            >
              <div className="container mx-auto px-4 py-4 max-h-[70vh] overflow-y-auto">
                {/* Services Section */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                    Services
                  </h3>
                  <div className="grid grid-cols-2 gap-1">
                    {services.slice(0, 6).map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services#${service.slug}`}
                        onClick={closeMegaMenu}
                        className="flex items-center gap-2 p-2.5 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <DynamicIcon name={service.icon} className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">{service.title}</span>
                      </Link>
                    ))}
                  </div>
                  <Link 
                    to="/services" 
                    onClick={closeMegaMenu}
                    className="block px-2 mt-2 text-sm font-semibold text-primary"
                  >
                    View all services →
                  </Link>
                </div>
                
                {/* Company Section */}
                <div className="mb-4 pt-4 border-t border-border">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                    Company
                  </h3>
                  <div className="space-y-1">
                    <Link to="/about" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">About Us</span>
                    </Link>
                    <Link to="/service-areas" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Service Areas</span>
                    </Link>
                    <Link to="/careers" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Careers</span>
                    </Link>
                  </div>
                </div>
                
                {/* Resources Section */}
                <div className="mb-4 pt-4 border-t border-border">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                    Resources
                  </h3>
                  <div className="space-y-1">
                    <Link to="/resources#faq" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">FAQ</span>
                    </Link>
                    <Link to="/promos" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <Award className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Promos</span>
                    </Link>
                    <Link to="/resources" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Blog & Tips</span>
                    </Link>
                  </div>
                </div>
                
                {/* Contact & CTA */}
                <div className="pt-4 border-t border-border">
                  <Link to="/contact" onClick={closeMegaMenu} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 mb-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Contact Us</span>
                  </Link>
                  
                  {/* Mobile Template Switcher */}
                  {!previewMode && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                        Choose Template
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {templateList.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => {
                              setTemplateId(t.id);
                              setIsMobileMenuOpen(false);
                            }}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                              templateId === t.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-foreground hover:bg-muted/80'
                            )}
                          >
                            <DynamicIcon name={t.icon} className="h-4 w-4" />
                            <span className="truncate">{t.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <a
                      href={`tel:${template.contact.phone.replace(/\D/g, '')}`}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-muted rounded-lg font-semibold"
                    >
                      <Phone className="h-4 w-4" />
                      {template.contact.phone}
                    </a>
                    {integrationSettings.financing_enabled && integrationSettings.financing_url && (
                      <a
                        href={integrationSettings.financing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-3 border border-border rounded-lg font-semibold text-sm hover:bg-muted/50 transition-colors"
                      >
                        <DollarSign className="h-4 w-4" />
                        {integrationSettings.financing_cta_text || 'Apply for Financing'}
                      </a>
                    )}
                    <Button asChild className="w-full">
                      <Link to="/contact" onClick={closeMegaMenu}>Get Free Quote</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      {/* Spacer for fixed header */}
      <div className="h-28 lg:h-32" />
    </>
  );
};
